import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
  /**
   * Grow with the content instead of scrolling (default). The element never
   * shrinks below the CSS height, so `min-h-*` classes still set the floor —
   * pass `autoGrow={false}` for a fixed, internally scrolling editor.
   */
  autoGrow?: boolean;
  /** Stop growing after this many rows and scroll instead. */
  maxRows?: number;
}

/** Line box of a textarea, used to translate `maxRows` into pixels. */
function lineHeightOf(element: HTMLTextAreaElement): number {
  const styles = window.getComputedStyle(element);
  const parsed = Number.parseFloat(styles.lineHeight);
  if (Number.isFinite(parsed)) return parsed;
  const fontSize = Number.parseFloat(styles.fontSize);
  return Number.isFinite(fontSize) ? fontSize * 1.6 : 22;
}

/**
 * Multi-line field. Auto-grows by default — the composer, the onboarding
 * writing-sample step and every "describe your business" box rely on it.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, invalid, rows = 4, autoGrow = true, maxRows, onChange, value, ...props },
  forwardedRef,
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  const resize = useCallback(() => {
    const element = innerRef.current;
    if (!element || !autoGrow) return;
    element.style.height = 'auto';
    // A hidden element reports 0 — leave it alone rather than collapsing it.
    if (element.scrollHeight === 0) return;
    const cap = maxRows
      ? lineHeightOf(element) * maxRows +
        Number.parseFloat(window.getComputedStyle(element).paddingTop) * 2
      : Number.POSITIVE_INFINITY;
    const next = Math.min(element.scrollHeight, cap);
    element.style.height = `${next}px`;
    element.style.overflowY = element.scrollHeight > cap ? 'auto' : 'hidden';
  }, [autoGrow, maxRows]);

  // Layout effect keeps the first paint at the right height (no jump).
  useLayoutEffect(resize, [resize, value]);

  useEffect(() => {
    if (!autoGrow) return;
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [autoGrow, resize]);

  return (
    <textarea
      ref={setRef}
      rows={rows}
      value={value}
      aria-invalid={invalid || undefined}
      onChange={(event) => {
        resize();
        onChange?.(event);
      }}
      className={cn(
        'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm leading-relaxed text-fg',
        'transition-[border-color,box-shadow] duration-150 placeholder:text-subtle',
        autoGrow ? 'resize-none' : 'resize-y',
        'focus-visible:border-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35',
        'disabled:cursor-not-allowed disabled:bg-elevated disabled:opacity-60',
        invalid && 'border-danger focus-visible:border-danger focus-visible:ring-danger/30',
        className,
      )}
      {...props}
    />
  );
});
